import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Gauge,
  Inbox,
  Search,
  ShieldCheck,
  Server,
  Bot,
  CheckCheck,
  Boxes,
  SlidersHorizontal,
  Settings,
  Menu,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

type NavEntry = { title: string; url: string; icon: LucideIcon };

const PRIMARY_TABS: NavEntry[] = [
  { title: "Posture", url: "/posture", icon: Gauge },
  { title: "Triage", url: "/triage", icon: Inbox },
  { title: "Investigate", url: "/investigate", icon: Search },
  { title: "Fleet", url: "/fleet", icon: Server },
];

const MORE_ENTRIES: NavEntry[] = [
  { title: "Coverage", url: "/coverage", icon: ShieldCheck },
  { title: "AI · Posture", url: "/ai/posture", icon: Bot },
  { title: "AI · Approvals", url: "/ai/approvals", icon: CheckCheck },
  { title: "AI · Inventory", url: "/ai/inventory", icon: Boxes },
  { title: "AI · Controls", url: "/ai/controls", icon: SlidersHorizontal },
  { title: "Settings", url: "/settings", icon: Settings },
];


const isActive = (pathname: string, url: string) =>
  pathname === url || pathname.startsWith(url + "/");

export function MobileNav() {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  return (
    <nav
      aria-label="Primary"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-background/95 backdrop-blur-md md:hidden"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <ul className="grid grid-cols-5">
        {PRIMARY_TABS.map((tab) => {
          const active = isActive(location.pathname, tab.url);
          const Icon = tab.icon;
          return (
            <li key={tab.url} className="relative">
              <NavLink
                to={tab.url}
                className={cn(
                  "tap-feedback relative flex h-14 min-h-[44px] flex-col items-center justify-center gap-0.5 text-[11px] font-medium transition-colors",
                  active ? "text-primary" : "text-muted-foreground hover:text-foreground"
                )}
                aria-current={active ? "page" : undefined}
              >
                <Icon className={cn("h-5 w-5", active && "scale-110")} aria-hidden />
                <span>{tab.title}</span>
              </NavLink>
            </li>
          );
        })}
        <li>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                className="tap-feedback flex h-14 min-h-[44px] w-full flex-col items-center justify-center gap-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground"
                aria-label="Open more navigation"
              >
                <Menu className="h-5 w-5" aria-hidden />
                <span>More</span>
              </button>
            </SheetTrigger>
            <SheetContent
              side="bottom"
              className="max-h-[85svh] overflow-y-auto rounded-t-2xl"
            >
              <SheetHeader className="text-left">
                <SheetTitle>More</SheetTitle>
              </SheetHeader>
              <ul className="mt-4 divide-y divide-border">
                {MORE_ENTRIES.map((entry) => {
                  const active = isActive(location.pathname, entry.url);
                  const Icon = entry.icon;
                  return (
                    <li key={entry.url}>
                      <NavLink
                        to={entry.url}
                        onClick={() => setOpen(false)}
                        className={cn(
                          "tap-feedback flex min-h-[44px] items-center gap-3 py-3 text-[15px] transition-colors",
                          active ? "text-primary font-medium" : "text-foreground hover:text-primary"
                        )}
                      >
                        <Icon className="h-5 w-5 text-muted-foreground" aria-hidden />
                        <span>{entry.title}</span>
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </SheetContent>
          </Sheet>
        </li>
      </ul>
    </nav>
  );
}

export default MobileNav;
